/**
 * tweestaps.js — pure hulpfuncties voor de tweede stap bij inloggen (22-10-2026).
 *
 * Docenten en beheerders die met e-mail en wachtwoord inloggen, vullen daarna een
 * code uit een authenticator-app in (GoTrue MFA, TOTP). De sessie staat dan op AAL2.
 * Inloggen via Entree en leerlingen: geen tweede stap. De database beslist wie moet
 * (mijn_tweestaps_status, migratie 105); zie src/services/tweestapsService.js.
 */

/** Claims uit een JWT (alleen lezen; de server controleert het token). */
export function leesJwtClaims(token) {
  if (typeof token !== 'string') return null;
  const delen = token.split('.');
  if (delen.length !== 3 || !delen[1]) return null;
  try {
    const b64 = delen[1].replace(/-/g, '+').replace(/_/g, '/');
    const opgevuld = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(opgevuld).split('').map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const claims = JSON.parse(json);
    return claims && typeof claims === 'object' && !Array.isArray(claims) ? claims : null;
  } catch {
    return null;
  }
}

/** Account dat via Entree is aangemaakt (zie backend entree-identiteit.synthetischEmail). */
export function isEntreeEmail(email) {
  return /^entree_[a-z0-9]+@testwisely\.nl$/i.test(String(email || ''));
}

/**
 * Welk scherm moet deze gebruiker zien?
 *   'ok'          gewoon verder
 *   'code'        code uit de app invullen (er is een app gekoppeld)
 *   'koppelen'    eerst een app koppelen (de tweede stap is verplicht)
 *
 * @param {object} p
 * @param {string|null} p.aal          'aal1' | 'aal2' uit de JWT
 * @param {object|null} p.status       antwoord van mijn_tweestaps_status (null = onbekend)
 * @param {string|null} p.volgendeAal  nextLevel van supabase (alleen als status onbekend is)
 */
export function bepaalStap({ aal, status, volgendeAal = null }) {
  if (aal === 'aal2') return 'ok';
  if (status && typeof status === 'object') {
    if (!status.wachtwoord_account) return 'ok';
    if (status.heeft_factor) return 'code';
    if (status.verplicht) return 'koppelen';
    return 'ok';
  }
  // Database nog zonder migratie 105, of even onbereikbaar: alleen wie zelf een app
  // koppelde, moet de code invullen. De server houdt de rest tegen.
  return volgendeAal === 'aal2' ? 'code' : 'ok';
}

/** Alleen de cijfers, hoogstens 6: wat iemand in het codeveld typt of plakt. */
export function schoonCode(invoer) {
  return String(invoer || '').replace(/\D/g, '').slice(0, 6);
}

/** Geheime sleutel in groepjes van 4, om over te typen als scannen niet lukt. */
export function formatteerGeheim(geheim) {
  return String(geheim || '').replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

/** Tekstbestand met herstelcodes om te bewaren. */
export function herstelcodesAlsTekst(codes, email, datum = new Date()) {
  const dag = datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  return [
    'TestWisely herstelcodes',
    email ? `Account: ${email}` : null,
    `Gemaakt op: ${dag}`,
    '',
    'Telefoon kwijt? Log in met je wachtwoord en kies "Telefoon kwijt?".',
    'Elke code werkt één keer. Bewaar dit bestand op een veilige plek.',
    '',
    ...(codes || []),
    '',
  ].filter((r) => r !== null).join('\n');
}

/** Foutmelding van GoTrue in gewone woorden. */
export function tweestapsFoutMelding(fout) {
  const code = fout?.code || '';
  const tekst = String(fout?.message || '');
  if (code === 'mfa_verification_failed' || /invalid totp code/i.test(tekst)) {
    return 'Deze code klopt niet. Kijk of de tijd op je telefoon goed staat en probeer de nieuwe code.';
  }
  if (code === 'mfa_verification_rejected' || /te vaak/i.test(tekst)) {
    return tekst || 'Te vaak een verkeerde code. Wacht een kwartier en log dan opnieuw in.';
  }
  if (code === 'mfa_challenge_expired' || /expired/i.test(tekst)) {
    return 'De code is verlopen. Vul de nieuwe code uit de app in.';
  }
  if (code === 'over_request_rate_limit' || fout?.status === 429) {
    return 'Te veel pogingen. Wacht even en probeer het opnieuw.';
  }
  return 'Er ging iets mis. Probeer het opnieuw.';
}
